import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
// Straight from the file, not the barrel: the forum barrel reaches
// ThreadsTable, which imports this module back.
import { SeenBar } from '@/modules/forum/components/thread/SeenBar'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React from 'react'
import { EventNonRespondersPrompt } from './event-non-responders-prompt'
import type {
  EventNode,
  EventQuestionNode,
  EventRegistrationNode,
} from '../types.graphql'

interface Props {
  event: EventNode
}

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="rounded-lg border border-stone-200 px-3 py-1.5">
    <div className="text-xs text-stone-500">{label}</div>
    <div className="text-lg font-semibold text-stone-900">{value}</div>
  </div>
)

const answerCell = (
  question: EventQuestionNode,
  registration: EventRegistrationNode,
  labels: { yes: string; no: string }
) => {
  const answer = registration.answers.find(
    item => item.question.id === question.id
  )
  if (!answer?.value) return ''
  if (question.type === 'YES_NO')
    return answer.value === 'true' ? labels.yes : labels.no
  return answer.value
}

/**
 * The answers to an event. Rendered only when the backend hands them over,
 * which it does for the event creator and administrators.
 */
export const EventAttendees: React.FC<Props> = ({ event }) => {
  const translations = useTranslate({
    attending: 'events.attending',
    declined: 'events.declined',
    noResponse: 'events.no-response',
    registered: 'events.registered-count',
    seeNotAnswered: 'events.see-not-answered',
    registrations: 'events.registrations',
    nobody: 'events.nobody-yet',
    name: 'common.name',
    answer: 'events.answer',
    note: 'common.comment',
    yes: 'common.yes',
    no: 'common.no',
  })

  const addPrompt = usePrompt()

  const summary = event.summary
  const registrations = event.registrations ?? []
  const nonResponders = event.nonResponders ?? []

  if (!summary) return null

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-6">
        <button
          type="button"
          className="flex cursor-pointer flex-col gap-1 text-left"
          title={translations.seeNotAnswered}
          onClick={() =>
            void addPrompt<null>(resolve => (
              <EventNonRespondersPrompt
                nonResponders={nonResponders}
                onSubmit={resolve}
              />
            ))
          }
        >
          <span className="text-xs font-semibold uppercase text-stone-500">
            {translations.registered}
          </span>
          <SeenBar views={summary.attending} width="160px" />
        </button>

        <div className="flex flex-wrap gap-2">
          <Stat label={translations.attending} value={summary.attending} />
          <Stat label={translations.declined} value={summary.declined} />
          <Stat label={translations.noResponse} value={summary.noResponse} />

          {summary.questions
            .filter(question => question.type !== 'TEXT')
            .map(question => (
              <Stat
                key={question.questionId}
                label={question.label}
                value={question.yesCount ?? question.total ?? 0}
              />
            ))}
        </div>
      </div>

      <div>
        <h4 className="mt-0 mb-2 text-xs font-semibold uppercase text-stone-500">
          {translations.registrations}
        </h4>

        {registrations.length === 0 ? (
          <p className="m-0 text-sm text-stone-500">{translations.nobody}</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{translations.name}</TableHead>
                <TableHead>{translations.answer}</TableHead>
                {event.questions.map(question => (
                  <TableHead key={question.id}>{question.label}</TableHead>
                ))}
                <TableHead>{translations.note}</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {registrations.map(registration => (
                <TableRow key={registration.id}>
                  <TableCell>{registration.user?.fullName}</TableCell>
                  <TableCell>
                    {registration.status === 'ATTENDING'
                      ? translations.attending
                      : translations.declined}
                  </TableCell>
                  {event.questions.map(question => (
                    <TableCell key={question.id}>
                      {registration.status === 'ATTENDING'
                        ? answerCell(question, registration, {
                            yes: translations.yes,
                            no: translations.no,
                          })
                        : ''}
                    </TableCell>
                  ))}
                  <TableCell className="whitespace-pre-wrap">
                    {registration.note}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
